import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import './CommercialDashboard.css';

const CommercialDashboard = ({onLogout}) => {
    const [profile, setProfile] = useState(null);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('dashboard');

    const API_URL = import.meta.env.VITE_API_URL;
    const token = localStorage.getItem('token');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [profileRes, clientsRes] = await Promise.all([
                // /me utilise le JWT pour identifier le commercial connecté
                axios.get(`${API_URL}/commerciaux/me`, {
                    headers: {Authorization: `Bearer ${token}`}
                }),
                axios.get(`${API_URL}/clients/mes-clients`, {
                    headers: {Authorization: `Bearer ${token}`}
                })
            ]);
            setProfile(profileRes.data);
            setClients(clientsRes.data.data || []);
        } catch (err) {
            setError('Erreur lors du chargement des données');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [API_URL, token]);

    useEffect(() => {fetchData();}, [fetchData]);

    // userId dérivé du profil chargé via JWT
    const userId = profile?._id?.toString();

    const filtered = clients.filter(c =>
        `${c.prenom} ${c.nom} ${c.cin} ${c.telephone}`.toLowerCase().includes(search.toLowerCase())
    );

    // Calcul des stats avancées
    const myDossiers = clients.flatMap(c =>
        (c.dossiers || []).filter(d => {
            const comId = d.commercial?._id?.toString() || d.commercial?.toString();
            return comId === userId;
        })
    );
    const totalPuissance = myDossiers.reduce((s, d) => s + (d.puissance || 0), 0);
    const parStatut = {
        nouveau: myDossiers.filter(d => d.statut === 'nouveau').length,
        installe: myDossiers.filter(d => d.statut === 'installe').length,
        receptionne: myDossiers.filter(d => d.statut === 'receptionne').length,
    };
    const parPaiement = {
        comptant: myDossiers.filter(d => d.typePaiement === 'comptant').length,
        credit: myDossiers.filter(d => d.typePaiement === 'credit').length,
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

    const getClientDossier = (client) =>
        client.dossiers?.find(d => {
            const comId = d.commercial?._id?.toString() || d.commercial?.toString();
            return comId === userId;
        });

    if (loading) return (
        <div className="com-dash-wrapper">
            <div className="com-dash-loading"><div className="com-spinner" /><p>Chargement...</p></div>
        </div>
    );

    return (
        <div className="com-dash-wrapper">
            {/* Sidebar */}
            <aside className="com-sidebar">
                <div className="com-sidebar-logo">
                    <span className="com-logo-icon">⚡</span>
                    <span className="com-logo-text">SASTRA</span>
                </div>

                <div className="com-sidebar-profile">
                    <div className="com-sidebar-avatar">
                        {profile?.prenom?.[0] || '?'}{profile?.nom?.[0] || ''}
                    </div>
                    <p className="com-sidebar-name">{profile?.prenom} {profile?.nom}</p>
                    <span className="com-sidebar-role">Commercial</span>
                </div>

                <nav className="com-sidebar-nav">
                    <button
                        className={`com-nav-item${activeTab === 'dashboard' ? ' active' : ''}`}
                        onClick={() => setActiveTab('dashboard')}
                    >
                        <span>📊</span> Dashboard
                    </button>
                    <button
                        className={`com-nav-item${activeTab === 'clients' ? ' active' : ''}`}
                        onClick={() => setActiveTab('clients')}
                    >
                        <span>👥</span> Mes Clients
                        <span className="com-nav-badge">{clients.length}</span>
                    </button>
                </nav>

                <button className="com-logout-btn" onClick={onLogout}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Déconnexion
                </button>
            </aside>

            {/* Main */}
            <main className="com-dash-main">
                {error && <div className="com-alert-error">{error}</div>}

                {/* ── ONGLET DASHBOARD ── */}
                {activeTab === 'dashboard' && (
                    <>
                        <div className="com-page-header">
                            <h1 className="com-page-title">Tableau de bord</h1>
                            <p className="com-page-sub">Bienvenue, {profile?.prenom} 👋</p>
                        </div>

                        {/* KPI Cards */}
                        <div className="com-kpi-grid">
                            <div className="com-kpi-card com-kpi-blue">
                                <div className="com-kpi-icon">👥</div>
                                <div className="com-kpi-content">
                                    <div className="com-kpi-value">{profile?.nbrClients || clients.length}</div>
                                    <div className="com-kpi-label">Total Clients</div>
                                </div>
                            </div>
                            <div className="com-kpi-card com-kpi-green">
                                <div className="com-kpi-icon">⚡</div>
                                <div className="com-kpi-content">
                                    <div className="com-kpi-value">{totalPuissance.toFixed(1)} kWc</div>
                                    <div className="com-kpi-label">Puissance Totale</div>
                                </div>
                            </div>
                            <div className="com-kpi-card com-kpi-gold">
                                <div className="com-kpi-icon">📁</div>
                                <div className="com-kpi-content">
                                    <div className="com-kpi-value">{myDossiers.length}</div>
                                    <div className="com-kpi-label">Dossiers gérés</div>
                                </div>
                            </div>
                            <div className="com-kpi-card com-kpi-purple">
                                <div className="com-kpi-icon">💰</div>
                                <div className="com-kpi-content">
                                    <div className="com-kpi-value">{profile?.pourcentage || 0}%</div>
                                    <div className="com-kpi-label">Commission</div>
                                </div>
                            </div>
                        </div>

                        {/* Graphes */}
                        <div className="com-charts-row">
                            {/* Statut dossiers */}
                            <div className="com-chart-card">
                                <h3 className="com-chart-title">📋 Statut des dossiers</h3>
                                <div className="com-bar-chart">
                                    {[
                                        {label: 'Nouveaux', value: parStatut.nouveau, color: '#3b82f6'},
                                        {label: 'Installés', value: parStatut.installe, color: '#10b981'},
                                        {label: 'Réceptionnés', value: parStatut.receptionne, color: '#f59e0b'},
                                    ].map(item => (
                                        <div key={item.label} className="com-bar-row">
                                            <span className="com-bar-label">{item.label}</span>
                                            <div className="com-bar-track">
                                                <div
                                                    className="com-bar-fill"
                                                    style={{
                                                        width: myDossiers.length > 0
                                                            ? `${(item.value / myDossiers.length) * 100}%`
                                                            : '0%',
                                                        background: item.color
                                                    }}
                                                />
                                            </div>
                                            <span className="com-bar-count">{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Type paiement */}
                            <div className="com-chart-card">
                                <h3 className="com-chart-title">💳 Type de paiement</h3>
                                <div className="com-donut-section">
                                    <div className="com-donut-legend">
                                        <div className="com-legend-row">
                                            <span className="com-legend-dot" style={{background: '#1e3a5f'}} />
                                            <span>Comptant</span>
                                            <strong>{parPaiement.comptant}</strong>
                                        </div>
                                        <div className="com-legend-row">
                                            <span className="com-legend-dot" style={{background: '#ffd700'}} />
                                            <span>Crédit</span>
                                            <strong>{parPaiement.credit}</strong>
                                        </div>
                                    </div>
                                    <div className="com-donut-visual">
                                        {myDossiers.length > 0 ? (
                                            <svg viewBox="0 0 36 36" className="com-donut-svg">
                                                <circle cx="18" cy="18" r="15.9" fill="transparent" stroke="#1e3a5f" strokeWidth="3.8"
                                                    strokeDasharray={`${(parPaiement.comptant / myDossiers.length) * 100} ${100 - (parPaiement.comptant / myDossiers.length) * 100}`}
                                                    strokeDashoffset="25" />
                                                <circle cx="18" cy="18" r="15.9" fill="transparent" stroke="#ffd700" strokeWidth="3.8"
                                                    strokeDasharray={`${(parPaiement.credit / myDossiers.length) * 100} ${100 - (parPaiement.credit / myDossiers.length) * 100}`}
                                                    strokeDashoffset={`${25 - (parPaiement.comptant / myDossiers.length) * 100}`} />
                                                <text x="18" y="20.35" textAnchor="middle" fontSize="6" fill="#1e3a5f" fontWeight="bold">
                                                    {myDossiers.length}
                                                </text>
                                            </svg>
                                        ) : (
                                            <div className="com-donut-empty">Aucun dossier</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Derniers clients */}
                            <div className="com-chart-card com-chart-card-wide">
                                <h3 className="com-chart-title">🕒 Derniers clients</h3>
                                <div className="com-recent-list">
                                    {clients.slice(0, 5).map(c => {
                                        const d = getClientDossier(c);
                                        return (
                                            <div key={c._id} className="com-recent-row">
                                                <div className="com-recent-avatar">{c.prenom?.[0]}{c.nom?.[0]}</div>
                                                <div className="com-recent-info">
                                                    <span className="com-recent-name">{c.prenom} {c.nom}</span>
                                                    <span className="com-recent-ref">{d?.referenceSteg || '—'}</span>
                                                </div>
                                                {d && (
                                                    <span className={`com-status-mini com-status-${d.statut}`}>
                                                        {d.statut === 'nouveau' ? 'En cours' : d.statut === 'installe' ? 'Installé' : 'Réceptionné'}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {clients.length === 0 && <p className="com-no-data">Aucun client récent</p>}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ── ONGLET CLIENTS ── */}
                {activeTab === 'clients' && (
                    <>
                        <div className="com-page-header">
                            <div>
                                <h1 className="com-page-title">Mes Clients</h1>
                                <p className="com-page-sub">{filtered.length} client{filtered.length !== 1 ? 's' : ''}</p>
                            </div>
                            <input
                                className="com-search"
                                type="text"
                                placeholder="🔍 Rechercher..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>

                        {filtered.length === 0 ? (
                            <div className="com-empty">
                                <div className="com-empty-icon">📂</div>
                                <h3>Aucun client trouvé</h3>
                                <p>Vous n'avez pas encore de clients assignés.</p>
                            </div>
                        ) : (
                            <div className="com-clients-table-wrap">
                                <table className="com-clients-table">
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            <th>CIN</th>
                                            <th>Téléphone</th>
                                            <th>Référence STEG</th>
                                            <th>Puissance</th>
                                            <th>District</th>
                                            <th>Statut</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(client => {
                                            const d = getClientDossier(client);
                                            return (
                                                <tr key={client._id}>
                                                    <td>
                                                        <div className="com-table-client">
                                                            <div className="com-table-avatar">{client.prenom?.[0]}{client.nom?.[0]}</div>
                                                            <div>
                                                                <p className="com-table-name">{client.prenom} {client.nom}</p>
                                                                <p className="com-table-email">{client.email || '—'}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td><code>{client.cin}</code></td>
                                                    <td>{client.telephone || '—'}</td>
                                                    <td>{d?.referenceSteg || '—'}</td>
                                                    <td>{d?.puissance ? `${d.puissance} kWc` : '—'}</td>
                                                    <td>
                                                        {d?.district
                                                            ? (typeof d.district === 'object' ? d.district.region : d.district)
                                                            : '—'}
                                                    </td>
                                                    <td>
                                                        {d ? (
                                                            <span className={`com-badge com-badge-${d.statut}`}>
                                                                {d.statut === 'nouveau' ? 'En cours' : d.statut === 'installe' ? 'Installé' : 'Réceptionné'}
                                                            </span>
                                                        ) : '—'}
                                                    </td>
                                                    <td>{d ? formatDate(d.dateArrivDossier) : '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default CommercialDashboard;
